import { useMutation } from "@apollo/client";
import * as Avatar from "@radix-ui/react-avatar";
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  BookOpen,
  CalendarHeart,
  CheckCheck,
  FileText,
  Gift,
  Grid3X3,
  Images,
  Info,
  LayoutGrid,
  List,
  MailPlus,
  Menu as MenuIcon,
  PartyPopper,
  ShieldBan,
  Users,
} from "lucide-react";
import moment from "moment";
import {
  ReactElement,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import inawoLuxeLogoImage from "src/assets/images/inawo-luxe-logo.svg";
import AnnouncementBanner from "src/components/AnnouncementBanner";
import Button from "src/components/Button";
import CustomDropdown from "src/components/CustomDropdown";
import DeleteModal from "src/components/DeleteModal";
import HeadlessDialog from "src/components/HeadlessDialog";
import Icon from "src/components/Icon";
import { InawoLuxeIcon } from "src/components/icons";
import {
  AsoEbiIcon,
  Chart,
  Global,
  LiveHall,
  Menu,
  People,
  PersonalCard,
  Wallet,
} from "src/components/icons/dashboard";
import InnerLoading from "src/components/InnerLoading";
import PageLoader from "src/components/PageLoader";
import { AppContext, ValueProps } from "src/context";
import { PlusOneRequestsContext } from "src/context/PlusOneRequestsContext";
import { REMOVE_TEAM_MEMBER } from "src/graphql/mutations/event.mutation";
import { GET_PROFILE_DETAILS } from "src/graphql/queries/auth.queries";
import {
  EVENT_CUSTOM_DOMAIN_STATUSES,
  EVENT_PARTS,
  GET_EVENT,
  GET_EVENTS_FOR_DASHBOARD,
  GET_TEAM_MEMBER,
} from "src/graphql/queries/event.queries";
import { PLUS_ONE_REQUESTS } from "src/graphql/queries/invitations.queries";
import { getRefreshToken } from "src/helper/storage";
import useCustomQuery from "src/hooks/useCustomQuery";
import useWindowDimensions from "src/hooks/useWindowDimensions";
import { Pencil, Trash } from "src/pages/aso-ebi/Icons";
import useAppStore from "src/store/utils";
import {
  Currency,
  EventCustomDomainStatusesQuery,
  EventCustomDomainStatusesQueryVariables,
  EventOwnerRole,
  EventPartsQuery,
  EventPartsQueryVariables,
  EventQuery,
  EventQueryVariables,
  GetEventsForDashboardQuery,
  GetEventsForDashboardQueryVariables,
  GetTeamMemberQuery,
  GetTeamMemberQueryVariables,
  PlusOneRequestsQuery,
  PlusOneRequestsQueryVariables,
  RemoveTeamMemberMutation,
  RemoveTeamMemberMutationVariables,
  UserQuery,
} from "src/types/api.d";
import { LUXE_GATE_DATE } from "src/utils";
import { v4 as uuidv4 } from "uuid";
import AddTeamMemberModal from "./AddTeamMember";
import styles from "./dashboard.module.scss";
import EditTeamMemberModal from "./EditTeamMember";

const IS_PRODUCTION = process.env.REACT_APP_INAWO_ENV === "live";

// Preload all dashboard route chunks during idle time so sidebar
// navigation feels instant after the initial load.
const preloadDashboardRoutes = () => {
  const routes = [
    () => import("src/pages/my-plan"),
    () => import("src/pages/invitations"),
    () => import("src/pages/event-details"),
    () => import("src/pages/event-schedule"),
    () => import("src/pages/access-control"),
    () => import("src/pages/reservations"),
    () => import("src/pages/event-vendors"),
    () => import("src/pages/gifts"),
    () => import("src/pages/wallet"),
    () => import("src/pages/website"),
    () => import("src/pages/rsvp"),
    () => import("src/pages/live-hall"),
    () => import("src/pages/guests"),
    () => import("src/pages/photobook"),
    () => import("src/pages/aso-ebi"),
    () => import("src/pages/aso-ebi-group"),
    () => import("src/pages/chapters"),
    () => import("src/pages/guides"),
  ];
  routes.forEach((load) => load());
};

interface DashboardProps {
  // pageName is now optional since we'll auto-determine it
  pageName?: React.ReactNode | string;
}

interface Links {
  url: string;
  name: string;
  Icon?: ReactElement | null;
  isPremiumFeature?: boolean;
  beta?: boolean;
  comingSoon?: boolean;
}

const BETA_BADGE_CONTENT = (
  <p>
    This is a new feature and we're still improving it. Your feedback helps!
    It's safe to use.
  </p>
);

interface BetaBadgeProps {
  isMobile: boolean;
  side: "top" | "right" | "bottom";
  triggerClassName: string;
  preventNavigation?: boolean;
}

const BetaBadge = ({
  isMobile,
  side,
  triggerClassName,
  preventNavigation = false,
}: BetaBadgeProps) => {
  const contentClassName =
    "text-secondary-9 max-w-[220px] rounded-md border border-secondary-6 bg-white px-3 py-2 text-left text-xs font-normal shadow-lg";

  const trigger = (
    <span
      className={triggerClassName}
      onClick={
        preventNavigation
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
            }
          : undefined
      }
      onPointerDown={preventNavigation ? (e) => e.stopPropagation() : undefined}
    >
      beta
      <Info className="h-3 w-3" />
    </span>
  );

  if (isMobile) {
    return (
      <Popover.Root>
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
        <Popover.Content
          side={side}
          className={contentClassName}
          sideOffset={8}
        >
          {BETA_BADGE_CONTENT}
        </Popover.Content>
      </Popover.Root>
    );
  }

  return (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
        <Tooltip.Content side={side} className={contentClassName}>
          {BETA_BADGE_CONTENT}
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

const MOBILE_BREAKPOINT = 900;

const DashboardLayout = ({ pageName }: DashboardProps = {}) => {
  const { sidebarLayout: layout, setSidebarLayout: setLayout } = useAppStore(
    (state) => state
  );

  const PryColor: string = "#34544f";

  const SecColor: string = "#808080";

  useEffect(() => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      (
        window as Window &
          typeof globalThis & { requestIdleCallback: (cb: () => void) => void }
      ).requestIdleCallback(preloadDashboardRoutes);
    } else {
      const t = setTimeout(preloadDashboardRoutes, 300);
      return () => clearTimeout(t);
    }
  }, []);

  const { width } = useWindowDimensions();
  // const isMobile = !useIsDesktop();
  const isMobile = width <= MOBILE_BREAKPOINT;

  const navigate = useNavigate();
  const [toggle, setToggle] = useState<boolean>(false);
  const [floatButton, setFloatButton] = useState<boolean>(false);
  const { pathname, search } = useLocation();
  const [isPageVisible, setIsPageVisible] = useState(
    () => document.visibilityState === "visible"
  );
  const contextValue = useContext<ValueProps | null>(AppContext);
  const storedUserRole = localStorage.getItem(
    "userRole"
  ) as EventOwnerRole | null;
  const effectiveUserRole = storedUserRole || contextValue?.userRole;
  const isRoleResolved = !!effectiveUserRole;
  const [dropdown, setDropdown] = useState<boolean>(false);
  const [openAddTeamModal, setOpenAddTeamModal] = useState<boolean>(false);
  const [openEditTeamModal, setOpenEditTeamModal] = useState<boolean>(false);
  const [openRemoveTeamModal, setOpenRemoveTeamModal] =
    useState<boolean>(false);
  const [editingTeamMember, setEditingTeamMember] = useState<
    GetTeamMemberQuery["getTeamMember"][number] | null
  >(null);
  const [deletingTeamMember, setDeletingTeamMember] = useState<
    GetTeamMemberQuery["getTeamMember"][number] | null
  >(null);
  const [selectedEventPartId, setSelectedEventPartId] = useState<number | null>(
    null
  );
  const [countdownTarget, setCountdownTarget] = useState<moment.Moment | null>(
    null
  );
  const [countdown, setCountdown] = useState<{
    months: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const addTeam = contextValue?.addTeam;
  const setAddTeam = contextValue?.setAddTeam;
  const isAdmin = contextValue?.isAdmin;
  const setIsAdmin = contextValue?.setIsAdmin;
  const setUserRole = contextValue?.setUserRole;
  const isLuxeEvent = contextValue?.isLuxeEvent;
  const setIsLuxeEvent = contextValue?.setIsLuxeEvent;
  const setIsLegacyEvent = contextValue?.setIsLegacyEvent;
  const setEventName = contextValue?.setEventName;
  const eventName = contextValue?.eventName;
  const refreshToken = getRefreshToken();
  const splitLocation = pathname.split("/");
  const { event, setEvent, hasDashboardLoaded, setHasDashboardLoaded } =
    useAppStore((state) => state);
  const plusOneRequestsQuery = useCustomQuery<
    PlusOneRequestsQuery,
    PlusOneRequestsQueryVariables
  >({
    query: PLUS_ONE_REQUESTS,
    variables: { eventId: event.uuid },
    skip: !event.uuid,
  });
  const {
    data: plusOneRequestsData,
    refetch: refetchPlusOneRequests,
    startPolling: startPlusOnePolling,
    stopPolling: stopPlusOnePolling,
  } = plusOneRequestsQuery;
  const isViewingPlusOne =
    pathname === "/invitations" &&
    new URLSearchParams(search).get("tab") === "plusOne";

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === "visible";
      setIsPageVisible(visible);
      if (!visible) {
        stopPlusOnePolling();
      } else if (event.uuid) {
        refetchPlusOneRequests().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [event.uuid, refetchPlusOneRequests, stopPlusOnePolling]);

  useEffect(() => {
    if (event.uuid && isPageVisible) {
      startPlusOnePolling(isViewingPlusOne ? 60000 : 300000);
    } else {
      stopPlusOnePolling();
    }
    return () => stopPlusOnePolling();
  }, [
    event.uuid,
    isPageVisible,
    isViewingPlusOne,
    startPlusOnePolling,
    stopPlusOnePolling,
  ]);

  useEffect(() => {
    if (
      event.uuid &&
      document.visibilityState === "visible" &&
      isViewingPlusOne
    ) {
      refetchPlusOneRequests().catch(() => {});
    }
  }, [event.uuid, isViewingPlusOne, refetchPlusOneRequests]);
  const pendingPlusOneCount =
    plusOneRequestsData?.plusOneRequests.filter(
      (request) => request.status.toLowerCase() === "pending"
    ).length ?? 0;
  const {
    data: customDomainStatusesData,
    isLoading: customDomainStatusesLoading,
  } = useCustomQuery<
    EventCustomDomainStatusesQuery,
    EventCustomDomainStatusesQueryVariables
  >({
    query: EVENT_CUSTOM_DOMAIN_STATUSES,
    variables: { eventId: event.uuid },
    skip: !IS_PRODUCTION || !event.uuid,
  });
  const attachedCustomDomain =
    customDomainStatusesData?.eventCustomDomainStatuses.find(
      (domain) => domain.status === "ATTACHED"
    )?.domain_name;
  const [ongoing, setOngoing] = useState<boolean>(false);

  const setIsPaymentSetup = contextValue?.setIsPaymentSetup;
  const setIsPaymentSetupLoading = contextValue?.setIsPaymentSetupLoading;

  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  // Handle touch events for mobile (close sidebar on left swipe)
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const deltaY = e.changedTouches[0].clientY - touchStartY.current;

      // Only trigger if horizontal swipe is dominant and exceeds threshold
      if (deltaX < -50 && Math.abs(deltaX) > Math.abs(deltaY)) {
        setToggle(false);
      }

      touchStartX.current = null;
      touchStartY.current = null;
    },
    [setToggle]
  );

  const setActiveLink = useCallback(
    (path: string) => {
      return splitLocation[1] === path ? true : false;
    },
    [splitLocation]
  );

  // Auto-determine page name based on current route
  const getPageName = useCallback(() => {
    if (pageName) return pageName; // Use prop if provided

    const currentPath = splitLocation[1];
    const secondPath = splitLocation[2]; // For routes like /aso-ebi/:groupId

    const pageNameMap: Record<string, string> = {
      "my-plan": "My Plan",
      dashboard: "My Plan",
      guests: "Guests",
      invitations: "Invitations",
      rsvp: "RSVP",
      "event-schedule": "Event Schedule",
      "access-control": "Access Control",
      "event-details": "Event Details",
      wishlist: "Wishlist",
      "cash-gifts": "Cash Gifts",
      "aso-ebi": secondPath ? "Aso Ebi Group" : "Aso Ebi", // Handle dynamic route
      "event-vendors": "Event Vendors",
      website: "Website",
      "live-hall": "Live Hall",
      photobook: "Photobook",
      chapters: "Chapters",
      guides: "Guides",
      reservation: "Seating Charts",
    };

    return pageNameMap[currentPath] || "Dashboard";
  }, [pageName, splitLocation]);

  const openSidebar = {
    width: toggle ? `100%` : `0px`,
    transform: toggle ? `translateX(${0}px` : `translateX(${-500}px`,
    opacity: toggle ? `1` : `0`,
  };

  const closeSidebar = {
    transform: "none",
    opacity: "1",
  };

  const changeOpacity = {
    display: "flex",
    opacity: "1",
  };

  const routeToLobby = () => {
    navigate("/events");
    setDropdown(!dropdown);
    setToggle(false);
  };

  const handleLink = (id: number, uuid: string) => {
    navigate("/my-plan");

    setEvent({ id, uuid });
  };

  const handlePreviewClick = useCallback(() => {
    if (!event?.slug || customDomainStatusesLoading) return;

    const websiteOrigin =
      IS_PRODUCTION && attachedCustomDomain
        ? `https://${attachedCustomDomain}`
        : `https://${event.slug}.${process.env.REACT_APP_INAWO_WEDDING_SITE_END}`;
    const previewUrl = `${websiteOrigin}?jelfo=og5xovf7i8f6ic678h_hk_8nl&evd=${refreshToken}&ppptf=ew2n498hdae3dd2h3a0gh24&couple=true`;
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  }, [
    attachedCustomDomain,
    customDomainStatusesLoading,
    event?.slug,
    refreshToken,
  ]);

  const links: Links[] = [
    {
      url: "my-plan",
      name: "My Plan",
      Icon: <Chart fill={setActiveLink("my-plan") ? PryColor : SecColor} />,
      isPremiumFeature: false,
    },
    {
      url: "event-details",
      name: "Event Details",
      Icon: (
        <PartyPopper
          color={setActiveLink("event-details") ? PryColor : SecColor}
        />
      ),
      isPremiumFeature: false,
    },
    {
      url: "event-schedule",
      name: "Event Schedule",
      Icon: (
        <CalendarHeart
          color={setActiveLink("event-schedule") ? PryColor : SecColor}
        />
      ),
      isPremiumFeature: false,
    },
    {
      url: "cash-gifts",
      name: "Cash Gifts",
      Icon: <Wallet fill={setActiveLink("cash-gifts") ? PryColor : SecColor} />,
    },
    {
      url: "wishlist",
      name: "Wishlist",
      Icon: <Gift color={setActiveLink("wishlist") ? PryColor : SecColor} />,
    },
    {
      url: "invitations",
      name: "Invitations",
      Icon: (
        <PersonalCard
          fill={setActiveLink("invitations") ? PryColor : SecColor}
        />
      ),
      isPremiumFeature: false,
    },
    {
      url: "guests",
      name: "Guests",
      Icon: <Users color={setActiveLink("guests") ? PryColor : SecColor} />,
      isPremiumFeature: false,
    },
    {
      url: "access-control",
      name: "Access control",
      Icon: (
        <ShieldBan
          color={setActiveLink("access-control") ? PryColor : SecColor}
        />
      ),
      isPremiumFeature: true,
    },
    {
      url: "rsvp",
      name: "RSVP",
      Icon: <Menu fill={setActiveLink("rsvp") ? PryColor : SecColor} />,
      isPremiumFeature: false,
    },
    {
      url: "chapters",
      name: "Chapters",
      Icon: (
        <BookOpen color={setActiveLink("chapters") ? PryColor : SecColor} />
      ),
    },
    {
      url: "reservation",
      name: "Seating Charts",
      Icon: (
        <Grid3X3 color={setActiveLink("reservation") ? PryColor : SecColor} />
      ),
      // beta: true,
      isPremiumFeature: true,
    },
    {
      url: "guides",
      name: "Guides",
      Icon: <FileText color={setActiveLink("guides") ? PryColor : SecColor} />,
      // comingSoon: true,
      isPremiumFeature: true,
    },
    {
      url: "aso-ebi",
      name: "Aso Ebi",
      Icon: (
        <AsoEbiIcon fill={setActiveLink("aso-ebi") ? PryColor : SecColor} />
      ),
    },
    {
      url: "event-vendors",
      name: "Event Vendors",
      Icon: (
        <People fill={setActiveLink("event-vendors") ? PryColor : SecColor} />
      ),
    },
    {
      url: "website",
      name: "Website",
      Icon: <Global fill={setActiveLink("website") ? PryColor : SecColor} />,
    },
    {
      url: "live-hall",
      name: "Live Hall",
      Icon: (
        <LiveHall fill={setActiveLink("live-hall") ? PryColor : SecColor} />
      ),
    },
    {
      url: "photobook",
      name: "Photobook",
      Icon: <Images color={setActiveLink("photobook") ? PryColor : SecColor} />,
    },
  ];

  const betaPaths = links.filter((l) => l.beta).map((l) => l.url);
  const isCurrentPageBeta = betaPaths.includes(splitLocation[1]);

  const { isLoading: loadingDashboardEventsData, data: dashboardEventsData } =
    useCustomQuery<
      GetEventsForDashboardQuery,
      GetEventsForDashboardQueryVariables
    >({
      query: GET_EVENTS_FOR_DASHBOARD,
      variables: {
        isInvited: false,
        page: 1,
        pageSize: 200,
      },
      onCompleted() {
        setInitialLoad(false);
        setHasDashboardLoaded(true); // Persist load state
      },
    });

  const { isLoading: loadingTeamData, data: teamData } = useCustomQuery<
    GetTeamMemberQuery,
    GetTeamMemberQueryVariables
  >({
    query: GET_TEAM_MEMBER,
    skip: !event.id,
    variables: {
      eventId: event.id,
    },
  });

  const [removeTeamMemberFn, { loading: loadingRemoveTeamMember }] =
    useMutation<RemoveTeamMemberMutation, RemoveTeamMemberMutationVariables>(
      REMOVE_TEAM_MEMBER,
      {
        onCompleted() {
          toast.success(
            <p className="toast">
              You have successfully deleted the team member.
            </p>
          );
        },
        onError(error) {
          if (error?.message !== "unauthenticated") {
            toast.error(
              <p className="toast">{error?.message ?? "An error occured"}</p>
            );
          } else {
            navigate("/clear");
          }
        },
      }
    );

  useEffect(() => {
    // Close sidebar on route change (mobile)
    setToggle(false);
    const currentElement =
      !loadingDashboardEventsData &&
      !!dashboardEventsData &&
      dashboardEventsData.events?.find((item) => item?.id === event.id);

    if (currentElement) {
      if (setEventName) {
        setEventName(currentElement?.name);
      }
      setEvent({
        uuid: currentElement?.uuid,
        slug: currentElement?.slug!,
      });

      if (setIsLuxeEvent) {
        setIsLuxeEvent(currentElement?.luxe);
      }

      if (setIsLegacyEvent) {
        const eventCreatedAt = currentElement?.created_at
          ? new Date(currentElement.created_at)
          : null;
        setIsLegacyEvent(
          eventCreatedAt !== null && eventCreatedAt < LUXE_GATE_DATE
        );
      }

      // set the users role of the event
      setIsAdmin && setIsAdmin(currentElement?.is_admin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardEventsData, event.id, loadingDashboardEventsData]);

  const { data, isLoading: loading } = useCustomQuery<
    EventQuery,
    EventQueryVariables
  >({
    query: GET_EVENT,
    variables: {
      eventId: event?.id,
    },
  });

  useEffect(() => {
    setIsPaymentSetupLoading && setIsPaymentSetupLoading(loading); // Sync with query loading state
    const eventEnabledCurrencies = data?.event?.enabled_currencies;
    if (setIsPaymentSetup) {
      const currencyMap = new Map<Currency, boolean>();
      eventEnabledCurrencies?.forEach((currency) => {
        currencyMap.set(currency as Currency, true);
      });
      setIsPaymentSetup(currencyMap);
    }
  }, [
    data?.event?.enabled_currencies,
    loading,
    setIsPaymentSetup,
    setIsPaymentSetupLoading,
  ]);

  const handleRemoveTeamMember = (teamMemberId: string) => {
    removeTeamMemberFn({
      variables: {
        eventId: event.uuid,
        teamMemberId,
      },
      refetchQueries: [
        {
          query: GET_EVENTS_FOR_DASHBOARD,
          variables: {
            isInvited: false,
            page: 1,
            pageSize: 200,
          },
        },
        { query: GET_TEAM_MEMBER, variables: { eventId: event.id } },
      ],
    });
  };

  const { data: eventsData, isLoading: loadingCountdown } = useCustomQuery<
    EventPartsQuery,
    EventPartsQueryVariables
  >({
    query: EVENT_PARTS,
    variables: {
      eventId: event.id,
    },
  });

  // Set default selected event part to the soonest upcoming (or first if none upcoming)
  useEffect(() => {
    if (!eventsData?.eventParts?.length) {
      setSelectedEventPartId(null);
      return;
    }

    const now = moment();
    let closestPartId: number | null = null;
    let closestMs = Infinity;

    eventsData.eventParts.forEach((part) => {
      const eventStart = moment(part.time);
      const msLeft = eventStart.diff(now);
      if (msLeft > 0 && msLeft < closestMs) {
        closestMs = msLeft;
        closestPartId = part.id;
      }
    });

    const defaultId = closestPartId ?? eventsData.eventParts[0]?.id ?? null;
    const partIds = eventsData.eventParts.map((p) => p.id);

    setSelectedEventPartId((prev) =>
      prev === null || !partIds.includes(prev) ? defaultId : prev
    );
  }, [eventsData]);

  // Derive countdown target and ongoing from selected event part
  useEffect(() => {
    if (!eventsData?.eventParts?.length || selectedEventPartId == null) {
      setCountdownTarget(null);
      return;
    }

    const part = eventsData.eventParts.find(
      (p) => p.id === selectedEventPartId
    );
    if (!part) {
      setCountdownTarget(null);
      return;
    }

    const target = moment(part.time);
    setCountdownTarget(target);
    setOngoing(target.isSame(moment(), "day"));
  }, [eventsData, selectedEventPartId]);

  // Live countdown timer: months, days, hours, minutes, seconds
  useEffect(() => {
    if (!countdownTarget) {
      setCountdown(null);
      return;
    }

    const compute = () => {
      const now = moment();
      if (countdownTarget.isSameOrBefore(now)) {
        setCountdown(null);
        return;
      }
      const months = countdownTarget.diff(now, "months");
      const afterMonths = now.clone().add(months, "months");
      const days = countdownTarget.diff(afterMonths, "days");
      const afterDays = afterMonths.clone().add(days, "days");
      const hours = countdownTarget.diff(afterDays, "hours");
      const afterHours = afterDays.clone().add(hours, "hours");
      const minutes = countdownTarget.diff(afterHours, "minutes");
      const afterMinutes = afterHours.clone().add(minutes, "minutes");
      const seconds = countdownTarget.diff(afterMinutes, "seconds");

      setCountdown({
        months,
        days,
        hours,
        minutes,
        seconds,
      });
    };

    compute();
    const interval = setInterval(compute, 1000);
    return () => clearInterval(interval);
  }, [countdownTarget]);

  const { data: profileData } = useCustomQuery<UserQuery>({
    query: GET_PROFILE_DETAILS,
  });

  // Set user role based on their team membership in the current event
  useEffect(() => {
    if (!dashboardEventsData || !profileData?.user?.id) return;

    const currentEvent = dashboardEventsData.events?.find(
      (item) => item?.id === event.id
    );

    if (currentEvent?.team) {
      const userTeamMember = currentEvent.team.find(
        (member) => member?.user_uuid === profileData.user?.uuid
      );

      if (userTeamMember?.role && setUserRole) {
        setUserRole(userTeamMember.role as EventOwnerRole);
      }
    }
  }, [dashboardEventsData, profileData, event.id, setUserRole]);

  return (
    <PageLoader
      loading={loadingDashboardEventsData && initialLoad && !hasDashboardLoaded}
    >
      <div
        className={`transition-all duration-500 ease-in-out ${styles["dashboardLayout"]}`}
      >
        <div
          className={styles[`dashboardLayout__sidebar`]}
          // Handle touch events for mobile
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{
            ...(width <= 900 ? openSidebar : closeSidebar),
            width:
              width <= 900
                ? toggle
                  ? "85%"
                  : "0px"
                : layout === "grid"
                  ? "350px"
                  : "300px",
            pointerEvents: width <= 900 && !toggle ? "none" : "auto",
            visibility: width <= 900 && !toggle ? "hidden" : "visible",
          }}
        >
          <div className={styles["dashboardLayout__sidebar--mobile"]}>
            <span
              onClick={() => setToggle(!toggle)}
              className={styles["dashboardLayout__sidebar--mobile-close"]}
            >
              <Icon iconName="close2" />
            </span>

            <div
              className={`absolute left-3 top-0 cursor-pointer rounded-md border border-[#649991] p-1 transition-all duration-300 hover:ring-1 hover:ring-[#649991] xm:hidden`}
              onClick={() => {
                // setManualLayout(true);
                setLayout(layout === "grid" ? "list" : "grid");
              }}
            >
              {layout === "grid" ? (
                <List className="size-4 text-primary-5" />
              ) : (
                <LayoutGrid className="size-4 text-primary-5" />
              )}
            </div>

            <div className="flex w-full flex-col">
              <div className="flex w-full items-start justify-center xm:justify-between">
                <div
                  className={styles["dashboardLayout__sidebar--mobile-logo"]}
                  onClick={() => navigate("/events")}
                >
                  {isLuxeEvent ? (
                    <img
                      src={inawoLuxeLogoImage}
                      alt="inawo-luxe logo"
                      className="object-cover"
                    />
                  ) : (
                    <Icon iconName="logo" />
                  )}
                </div>
                <div
                  className={`${isLuxeEvent ? `mt-4` : `mt-2`} hidden cursor-pointer rounded-md border border-[#649991] p-1 transition-all duration-300 hover:ring-1 hover:ring-[#649991] xm:block`}
                  onClick={() => {
                    setLayout(layout === "grid" ? "list" : "grid");
                  }}
                >
                  {layout === "grid" ? (
                    <List className="size-4 text-primary-5" />
                  ) : (
                    <LayoutGrid className="size-4 text-primary-5" />
                  )}
                </div>
              </div>

              {/* event part countdown - only show if there are event parts */}
              {!!eventsData?.eventParts?.length && (
                <div className="mt-4 w-full min-w-0 rounded-xl border border-secondary-6 bg-secondary-5 p-3 shadow-sm">
                  <CustomDropdown
                    options={[...(eventsData.eventParts ?? [])]
                      .sort(
                        (a, b) =>
                          moment(a.time).valueOf() - moment(b.time).valueOf()
                      )
                      .map((part) => ({
                        label: part.name || part.part || `Event ${part.id}`,
                        value: String(part.id),
                      }))}
                    value={
                      selectedEventPartId != null
                        ? String(selectedEventPartId)
                        : ""
                    }
                    onChange={(value) =>
                      setSelectedEventPartId(value ? Number(value) : null)
                    }
                    placeholder="Select event part"
                  />
                  <div className="mt-3 border-t border-secondary-6 pt-3">
                    {loadingCountdown ? (
                      <p className="text-center text-sm text-secondary-3">
                        ...
                      </p>
                    ) : ongoing ? (
                      <p className="text-center text-sm font-medium text-primary-6">
                        Happening today
                      </p>
                    ) : countdown ? (
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {[
                          { val: countdown.months, label: "mo" },
                          { val: countdown.days, label: "d" },
                          { val: countdown.hours, label: "h" },
                          { val: countdown.minutes, label: "m" },
                          { val: countdown.seconds, label: "s" },
                        ].map(({ val, label }) => (
                          <span
                            key={label}
                            className="flex min-w-[2.25rem] flex-col items-center rounded-lg bg-white px-2 py-1.5 shadow-sm ring-1 ring-secondary-6/50"
                          >
                            <span className="text-sm font-semibold tabular-nums text-primary-6">
                              {String(val).padStart(2, "0")}
                            </span>
                            <span className="text-[10px] font-medium uppercase tracking-wider text-secondary-3">
                              {label}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-secondary-3">
                        Event ended
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* <div className="mt-4 hidden border-b border-t py-2 text-lg font-bold text-[#4A4A4A] xm:flex">
              {eventName ? `${eventName}` : "..."}
            </div> */}

            <Popover.Root>
              <Popover.Trigger className={styles["dashboardLayout__popover"]}>
                <div
                  className={`${styles["dashboardLayout__main__event-title"]} ${styles["dashboardLayout__main__event-title__aside"]}`}
                >
                  <p>
                    {loadingDashboardEventsData &&
                    !!dashboardEventsData &&
                    !hasDashboardLoaded ? (
                      <>&nbsp;</>
                    ) : eventName ? (
                      `${eventName}`
                    ) : (
                      "..."
                    )}
                  </p>
                  <Icon iconName="caretDouble" />
                </div>
              </Popover.Trigger>

              <Popover.Content
                className={styles["dashboardLayout__main__event-list"]}
              >
                {!loadingDashboardEventsData && !!dashboardEventsData && (
                  <div
                    className={styles["dropdown"]}
                    style={dropdown && width < 670 ? changeOpacity : {}}
                  >
                    <div className={styles["dropdown-item"]}>
                      <ul className={styles["dropdown-item--content"]}>
                        <li className={styles["dropdown-item--list--first"]}>
                          <p>{eventName ? `${eventName}` : "..."}</p>
                          {isLuxeEvent && (
                            <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-[#FFFFFF]">
                              <InawoLuxeIcon />
                            </span>
                          )}
                        </li>
                        {!!dashboardEventsData.events &&
                          dashboardEventsData.events
                            .filter((item) => item?.name !== eventName)
                            .map((data, _i) => (
                              <li
                                key={uuidv4()}
                                className={styles["dropdown-item--list"]}
                                onClick={() => handleLink(data!.id, data!.uuid)}
                              >
                                <span className="flex items-center justify-between">
                                  {data?.name}

                                  {data?.luxe && (
                                    <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-[#FFFFFF]">
                                      <InawoLuxeIcon />
                                    </span>
                                  )}
                                </span>
                              </li>
                            ))}
                        <li
                          onClick={routeToLobby}
                          className={styles["dropdown-item--list--last"]}
                        >
                          <Button
                            label="back to lobby"
                            type="button"
                            onClick={routeToLobby}
                          >
                            <Icon iconName="redArrowLeft" />
                            Back To Lobby
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </Popover.Content>
            </Popover.Root>
          </div>

          <ul className={styles["dashboardLayout__sidebar--links"]}>
            {!isRoleResolved ? (
              <li className="p-4 text-left text-sm text-gray-400">
                Loading...
              </li>
            ) : (
              (() => {
                const filteredLinks = links.filter((item) => {
                  // If user is asoebi, only show My Plan and Aso Ebi
                  if (effectiveUserRole === EventOwnerRole.Asoebi) {
                    return ["my-plan", "aso-ebi"].includes(item.url);
                  }

                  // if admin keep all links, otherwise hide Cash Gifts for non-admins
                  return isAdmin ? true : !["Cash Gifts"].includes(item?.name);
                });

                // Desktop layout: grid with first item spanning 2 rows vertically
                if (layout === "grid") {
                  return (
                    <div className={styles["sidebar-grid"]}>
                      {filteredLinks.map((link, index) => (
                        <Link
                          to={`/${link.url}`}
                          key={link.url}
                          onClick={() => setToggle(false)}
                          className={styles["sidebar-grid-item"]}
                        >
                          <div>
                            <li
                              className={`relative gap-2 border hover:ring-1 hover:ring-[#649991] ${
                                setActiveLink(link.url)
                                  ? "border-[#1A5F554F] bg-5 hover:ring-0"
                                  : ""
                              }`}
                            >
                              {link.Icon}
                              <span
                                className={
                                  setActiveLink(link.url)
                                    ? "font-semibold text-primary-5"
                                    : ""
                                }
                              >
                                {link.name}
                              </span>

                              {link?.isPremiumFeature && (
                                <div className="absolute right-3 top-3">
                                  <InawoLuxeIcon />
                                </div>
                              )}
                              {link?.beta && (
                                <BetaBadge
                                  isMobile={isMobile}
                                  side="top"
                                  triggerClassName="absolute -left-2 -top-2 flex w-fit cursor-help items-center gap-0.5 rounded bg-primary-5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white md:absolute md:-top-3 md:right-0"
                                  preventNavigation
                                />
                              )}
                              {link?.comingSoon && (
                                <span className="absolute -left-2 -top-2 rounded bg-primary-5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white md:absolute md:-top-3 md:right-0">
                                  coming soon!
                                </span>
                              )}
                            </li>
                          </div>
                        </Link>
                      ))}
                    </div>
                  );
                } else {
                  return filteredLinks.map((link) => (
                    <Link
                      to={`/${link.url}`}
                      key={link.url}
                      onClick={() => setToggle(false)}
                      className={`${styles["dashboardLayout__sidebar--links--list"]}`}
                    >
                      <li
                        className={`relative flex w-full items-center justify-between`}
                      >
                        <nav className="flex items-center gap-4">
                          {link.Icon}
                          <div className="flex items-center gap-2">
                            <span
                              className={
                                setActiveLink(link.url)
                                  ? "font-semibold text-primary-5"
                                  : ""
                              }
                            >
                              {link.name}
                            </span>

                            {link?.beta && (
                              <BetaBadge
                                isMobile={isMobile}
                                side="right"
                                triggerClassName="flex cursor-help items-center gap-0.5 rounded bg-primary-5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white"
                                preventNavigation
                              />
                            )}
                          </div>
                        </nav>
                        {link?.isPremiumFeature && <InawoLuxeIcon />}
                      </li>
                    </Link>
                  ));
                }
              })()
            )}

            {/* Rendering only in development */}
            {/* {process.env.REACT_APP_INAWO_ENV === "dev" && ( */}
            {/* <>
              <Link
                to={"/event-details?tab=preferences"}
                className={
                  styles["dashboardLayout__sidebar--links__preferences__title"]
                }
              >
                Preferences
              </Link>
              {preferences
                .filter((item) =>
                  // if live hall isn't turned on don't show it.
                  eventPreferences?.live_hall
                    ? item
                    : !["Live Hall"].includes(item.name)
                )
                .map((link) => (
                  <Link to={`/${link.url}`} key={uuidv4()}>
                    <li>
                      {link.Icon}
                      <span
                        className={
                          setActiveLink(link.url) ? styles["active"] : ""
                        }
                      >
                        {link.name}
                      </span>
                    </li>
                  </Link>
                ))}
            </> */}
            {/* )} */}
          </ul>
        </div>

        {width <= 900 && toggle && (
          <div
            className="fixed inset-0 z-[998] bg-[black]/40 transition-opacity"
            onClick={() => setToggle(false)}
          />
        )}

        <main className={styles["dashboardLayout__main"]}>
          <header className={styles["dashboardLayout__main--header"]}>
            <div
              className={`${
                styles["dashboardLayout__main--header-hamburger"]
              } ${styles["dashboardLayout__main--header-hamburger__other"]}`}
              onClick={() => setToggle(!toggle)}
            >
              <MenuIcon />
            </div>

            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold md:text-lg">
                {getPageName()}
              </h3>

              {isCurrentPageBeta && (
                <BetaBadge
                  isMobile={isMobile}
                  side="bottom"
                  triggerClassName="flex cursor-help items-center gap-0.5 rounded bg-primary-5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white"
                />
              )}
            </div>

            <div className={styles["dashboardLayout__main--header-wrapper"]}>
              <Tooltip.Provider delayDuration={200}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      type="button"
                      onClick={() => navigate("/invitations?tab=plusOne")}
                      className={`${styles["dashboardLayout__main--header-wrapper-plus-one-alert"]} relative grid h-10 w-10 place-items-center rounded-full text-gray-700 transition hover:bg-gray-100`}
                      aria-label={`${pendingPlusOneCount} pending plus-one requests`}
                    >
                      <MailPlus className="h-5 w-5" />
                      {pendingPlusOneCount > 0 && (
                        <span className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[11px] font-bold leading-5 text-white">
                          {pendingPlusOneCount > 99
                            ? "99+"
                            : pendingPlusOneCount}
                        </span>
                      )}
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Content
                    side="bottom"
                    sideOffset={8}
                    className="z-50 max-w-[220px] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-lg"
                  >
                    Manage guest plus-one requests.
                    {pendingPlusOneCount > 0 &&
                      ` ${pendingPlusOneCount} pending.`}
                    <Tooltip.Arrow className="fill-white" />
                  </Tooltip.Content>
                </Tooltip.Root>
              </Tooltip.Provider>

              <Popover.Root open={addTeam} onOpenChange={setAddTeam}>
                <Popover.Trigger className={styles["dashboardLayout__popover"]}>
                  {!loadingTeamData ? (
                    <Tooltip.Provider delayDuration={200}>
                      <Tooltip.Root>
                        <Tooltip.Trigger
                          className={styles["dashboardLayout__popover-tooltip"]}
                        >
                          <div
                            className={`${
                              styles["dashboardLayout__main__bubble"]
                            } ${
                              !setActiveLink("my-plan") &&
                              styles["dashboardLayout__main__bubble__other"]
                            }`}
                          >
                            <div>
                              <Avatar.Root
                                className={
                                  styles[
                                    "dashboardLayout__main__bubble__container"
                                  ]
                                }
                              >
                                {!!teamData?.getTeamMember[0].user?.image ? (
                                  <>
                                    <Avatar.Image
                                      src={
                                        teamData?.getTeamMember[0].user
                                          ?.image ?? ""
                                      }
                                      alt={`${teamData?.getTeamMember[0].user?.first_name}'s avatar`}
                                      className={
                                        styles[
                                          "dashboardLayout__main__bubble__img"
                                        ]
                                      }
                                    />
                                    <Avatar.Fallback
                                      delayMs={0}
                                      className={`${
                                        styles[
                                          "dashboardLayout__main__bubble-avatar"
                                        ]
                                      } ${
                                        !setActiveLink("my-plan") &&
                                        styles[
                                          "dashboardLayout__main__bubble__other-avatar"
                                        ]
                                      }`}
                                    >
                                      {`${
                                        !!teamData?.getTeamMember[0].user
                                          ?.first_name
                                          ? teamData?.getTeamMember[0].user
                                              ?.first_name[0]
                                          : ""
                                      }${
                                        !!teamData?.getTeamMember[0].user
                                          ?.last_name
                                          ? teamData?.getTeamMember[0].user
                                              ?.last_name[0]
                                          : ""
                                      }`}
                                    </Avatar.Fallback>
                                  </>
                                ) : (
                                  <span
                                    className={`${
                                      styles[
                                        "dashboardLayout__main__bubble-avatar"
                                      ]
                                    } ${
                                      !setActiveLink("my-plan") &&
                                      styles[
                                        "dashboardLayout__main__bubble__other-avatar"
                                      ]
                                    }`}
                                  >
                                    {`${
                                      !!teamData?.getTeamMember[0].user
                                        ?.first_name
                                        ? teamData?.getTeamMember[0].user
                                            ?.first_name[0]
                                        : ""
                                    }${
                                      !!teamData?.getTeamMember[0].user
                                        ?.last_name
                                        ? teamData?.getTeamMember[0].user
                                            ?.last_name[0]
                                        : ""
                                    }`}
                                  </span>
                                )}
                              </Avatar.Root>
                            </div>
                            + {teamData?.getTeamMember.length}
                          </div>
                        </Tooltip.Trigger>

                        <Tooltip.Content
                          className={
                            styles["dashboardLayout__main__bubble-content"]
                          }
                        >
                          See all the people involved in this wedding.
                          <Tooltip.Arrow
                            className={
                              styles["dashboardLayout__main__bubble-fill"]
                            }
                          />
                        </Tooltip.Content>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  ) : (
                    <></>
                  )}
                </Popover.Trigger>

                <Popover.Content
                  className={styles["dashboardLayout__main__team-content"]}
                >
                  <h4>Team Members</h4>

                  <div
                    className={
                      styles["dashboardLayout__main__team-content__body"]
                    }
                  >
                    {!loadingTeamData ? (
                      teamData?.getTeamMember.map((member) => (
                        <div
                          key={uuidv4()}
                          className={
                            styles[
                              "dashboardLayout__main__team-content__body-group"
                            ]
                          }
                        >
                          <div
                            className={
                              styles[
                                "dashboardLayout__main__team-content__body-group__details"
                              ]
                            }
                          >
                            <div className="flex items-center gap-2">
                              <h5>{`${member.user?.first_name} ${member.user?.last_name}`}</h5>
                              {member.user_uuid === profileData?.user?.uuid && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-primary-6/10 px-2 py-0.5 text-[11px] font-medium text-primary-6">
                                  <CheckCheck className="h-3.5 w-3.5" />
                                  You
                                </span>
                              )}
                            </div>
                            <p>{member.role}</p>
                          </div>
                          {member.user_uuid !== profileData?.user?.uuid &&
                            isAdmin && (
                              <div className="flex items-center gap-2">
                                <div
                                  className={`cursor-pointer ${
                                    styles[
                                      "dashboardLayout__main__team-content__body-group__btn"
                                    ]
                                  }`}
                                  onClick={() => {
                                    setEditingTeamMember(member);
                                    setOpenEditTeamModal(true);
                                  }}
                                >
                                  <Pencil />
                                </div>

                                <div
                                  onClick={() => {
                                    setDeletingTeamMember(member);
                                    setOpenRemoveTeamModal(true);
                                  }}
                                  className={
                                    styles[
                                      "dashboardLayout__main__team-content__body-group__btn"
                                    ]
                                  }
                                >
                                  <Trash />
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    ) : (
                      <></>
                    )}
                  </div>

                  {/* Edit Team Member Modal */}
                  <HeadlessDialog
                    isOpen={openEditTeamModal}
                    onClose={() => {
                      setOpenEditTeamModal(false);
                      // setEditingTeamMember(null);
                    }}
                    title="Edit Team Member"
                    description={`Edit the details of the team member ${editingTeamMember?.user?.first_name} ${editingTeamMember?.user?.last_name}`}
                    size="xl"
                  >
                    {editingTeamMember && (
                      <EditTeamMemberModal
                        handleGoBack={() => {
                          setEditingTeamMember(null);
                          setOpenEditTeamModal(false);
                        }}
                        teamMemberId={editingTeamMember?.id}
                        eventId={event?.id}
                        eventUuid={event?.uuid}
                        currentRole={editingTeamMember?.role as EventOwnerRole}
                        teamMemberName={`${editingTeamMember?.user?.first_name} ${editingTeamMember?.user?.last_name}`}
                      />
                    )}
                  </HeadlessDialog>

                  <HeadlessDialog
                    isOpen={openRemoveTeamModal}
                    onClose={() => {
                      setOpenRemoveTeamModal(false);
                      // setDeletingTeamMember(null);
                    }}
                  >
                    <DeleteModal
                      title="Remove Team Member?"
                      handleGoBack={() => {
                        setOpenRemoveTeamModal(false);
                        setDeletingTeamMember(null);
                      }}
                      handleDelete={() =>
                        !!deletingTeamMember?.id
                          ? handleRemoveTeamMember(deletingTeamMember?.id)
                          : null
                      }
                      loading={loadingRemoveTeamMember}
                      message={`Are you sure you want to remove ${deletingTeamMember?.user?.first_name} ${deletingTeamMember?.user?.last_name} from your wedding?`}
                    />
                  </HeadlessDialog>

                  <Button
                    type="button"
                    label="+ Add New Member"
                    design="secondary"
                    className="group-hover:text-white"
                    onClick={() => setOpenAddTeamModal(true)}
                  >
                    + Add New Member
                  </Button>

                  {effectiveUserRole !== EventOwnerRole.Asoebi && (
                    <HeadlessDialog
                      isOpen={openAddTeamModal}
                      onClose={() => setOpenAddTeamModal(false)}
                      title="Add New Team Member"
                      description="Add a new team member to your wedding"
                      size="xl"
                    >
                      <AddTeamMemberModal
                        id={event.id}
                        handleGoBack={() => setOpenAddTeamModal(false)}
                      />
                    </HeadlessDialog>
                  )}
                </Popover.Content>
              </Popover.Root>

              <Tooltip.Provider delayDuration={200}>
                <Tooltip.Root>
                  <Tooltip.Trigger
                    className={styles["dashboardLayout__popover-tooltip"]}
                  >
                    <button
                      type="button"
                      onClick={handlePreviewClick}
                      disabled={customDomainStatusesLoading}
                      aria-label="Preview your wedding website"
                      className={styles["dashboardLayout__preview"]}
                    >
                      {hasDashboardLoaded && event.slug && (
                        <Icon iconName="previewEye" />
                      )}
                    </button>
                  </Tooltip.Trigger>

                  <Tooltip.Content
                    className={styles["dashboardLayout__main__bubble-content"]}
                  >
                    Preview your wedding website.
                    <Tooltip.Arrow
                      className={styles["dashboardLayout__main__bubble-fill"]}
                    />
                  </Tooltip.Content>
                </Tooltip.Root>
              </Tooltip.Provider>

              <Popover.Root>
                <Popover.Trigger className={styles["dashboardLayout__popover"]}>
                  <div
                    className={`${styles["dashboardLayout__main__event-title"]} ${
                      // !setActiveLink("my-plan") &&
                      styles["dashboardLayout__main__event-title__other"]
                    }`}
                  >
                    <p>
                      {loadingDashboardEventsData &&
                      !!dashboardEventsData &&
                      !hasDashboardLoaded ? (
                        <>&nbsp;</>
                      ) : eventName ? (
                        `${eventName}`
                      ) : (
                        "..."
                      )}
                    </p>
                    <Icon iconName="caretDouble" />
                  </div>
                </Popover.Trigger>

                <Popover.Content
                  className={styles["dashboardLayout__main__event-list"]}
                >
                  {!loadingDashboardEventsData && !!dashboardEventsData && (
                    <div className={styles["dropdown"]}>
                      <div className={styles["dropdown-item"]}>
                        <ul className={styles["dropdown-item--content"]}>
                          <li className={styles["dropdown-item--list--first"]}>
                            <p>{eventName ? `${eventName}` : "..."}</p>
                            {isLuxeEvent && (
                              <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-[#FFFFFF]">
                                <InawoLuxeIcon />
                              </span>
                            )}
                          </li>
                          {!!dashboardEventsData.events &&
                            dashboardEventsData.events
                              .filter((item) => item?.name !== eventName)
                              .map((data, _i) => (
                                <li
                                  key={uuidv4()}
                                  className={styles["dropdown-item--list"]}
                                  onClick={() =>
                                    handleLink(data!.id, data!.uuid)
                                  }
                                >
                                  <span className="flex items-center justify-between">
                                    {data?.name}

                                    {data?.luxe && (
                                      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-[#FFFFFF]">
                                        <InawoLuxeIcon />
                                      </span>
                                    )}
                                  </span>
                                </li>
                              ))}
                          <li
                            onClick={routeToLobby}
                            className={styles["dropdown-item--list--last"]}
                          >
                            <Button
                              label="back to lobby"
                              type="button"
                              onClick={routeToLobby}
                            >
                              <Icon iconName="redArrowLeft" />
                              Back To Lobby
                            </Button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </Popover.Content>
              </Popover.Root>
            </div>
          </header>

          {/* Announcement Banner */}
          <div className="px-4 md:px-6">
            <AnnouncementBanner />
          </div>

          <section className={styles[`dashboardLayout__main--body`]}>
            <Suspense fallback={<InnerLoading />}>
              <PlusOneRequestsContext.Provider value={plusOneRequestsQuery}>
                <Outlet />
              </PlusOneRequestsContext.Provider>
            </Suspense>
          </section>
        </main>
      </div>
      <Popover.Root open={floatButton} onOpenChange={setFloatButton}>
        <Popover.Trigger
          className={styles["dashboardLayout__float"]}
          onClick={() => setFloatButton(true)}
        >
          {floatButton ? (
            <Icon iconName="close3" />
          ) : (
            <Icon iconName="caretDouble" />
          )}
        </Popover.Trigger>

        <Popover.Content
          side="top"
          className={`${styles["dashboardLayout__main__event-list"]} ${styles["dashboardLayout__main__fixed-list"]}`}
        >
          {!loadingDashboardEventsData && !!dashboardEventsData && (
            <div
              className={styles["dropdown"]}
              style={dropdown && width < 670 ? changeOpacity : {}}
            >
              <div className={styles["dropdown-item"]}>
                <ul className={styles["dropdown-item--content"]}>
                  <li className={styles["dropdown-item--list--first"]}>
                    <p>{eventName ? `${eventName}` : "..."}</p>
                    {isLuxeEvent && (
                      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-[#FFFFFF]">
                        <InawoLuxeIcon />
                      </span>
                    )}
                  </li>
                  {!!dashboardEventsData.events &&
                    dashboardEventsData.events
                      .filter((item) => item?.name !== eventName)
                      .map((data, _i) => (
                        <li
                          key={uuidv4()}
                          className={styles["dropdown-item--list"]}
                          onClick={() => handleLink(data!.id, data!.uuid)}
                        >
                          <span className="flex items-center justify-between">
                            {data?.name}

                            {data?.luxe && (
                              <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-[#FFFFFF]">
                                <InawoLuxeIcon />
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                  <li
                    onClick={routeToLobby}
                    className={styles["dropdown-item--list--last"]}
                  >
                    <Button
                      label="back to lobby"
                      type="button"
                      onClick={routeToLobby}
                    >
                      <Icon iconName="redArrowLeft" />
                      Back To Lobby
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </Popover.Content>
      </Popover.Root>
    </PageLoader>
  );
};

export default DashboardLayout;
